import axiosInstance from "../../common/config/axios";
import axios from "axios"; // Keep this for external API calls
import { deviceType } from "react-device-detect";
import { encryptionAuth } from "./authUtil";
import Swal from "sweetalert2";

/**
 * Google OAuth PKCE Utility Functions (Pure Implementation - No GIS)
 * Implements full PKCE flow without relying on Google Identity Services library
 */

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";

export function isGoogleLogin() {
  const urlParams = new URLSearchParams(window.location.search);

  if (!urlParams) {
    return false;
  }

  return urlParams.has("state")
      && urlParams.has("code")
      && urlParams.has("scope")
}

/**
 * Generate a cryptographically secure random string for PKCE code verifier.
 * @param {number} length - Length of the verifier (43-128 characters)
 * @returns {string} Base64 URL-encoded random string
 */
function generateCodeVerifier(length = 128) {
  if (length < 43 || length > 128) {
    throw new Error(
      "Code verifier length must be between 43 and 128 characters"
    );
  }

  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate code challenge from code verifier using S256 method.
 * @param {string} codeVerifier
 * @returns {Promise<string>} SHA256 hash of the verifier, base64 URL-encoded
 */
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Base64 URL encode a byte array.
 * @param {Uint8Array} array
 * @returns {string}
 */
function base64UrlEncode(array) {
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Generate a random state parameter for CSRF protection.
 * @returns {string}
 */
function generateState() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Build the Google authorization URL with PKCE parameters.
 * @param {string} codeChallenge
 * @param {string} state
 * @returns {string} Authorization URL
 */
function buildAuthorizationUrl(codeChallenge, state) {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: window.location.origin,
    response_type: "code",
    scope: "openid email profile",
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    access_type: "offline",
    prompt: "consent",
  });

  return `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
}

/**
 * Handle Google Login button click - Redirects to Google OAuth.
 * Stores PKCE parameters in sessionStorage and redirects to Google.
 *
 * @returns {Promise<void>}
 */
export async function handleGoogleLogin() {
  try {
    // Validate configuration
    if (!GOOGLE_CLIENT_ID) {
      throw new Error(
        "Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment."
      );
    }

    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    // Store PKCE parameters in sessionStorage for callback
    sessionStorage.setItem("google_code_verifier", codeVerifier);
    sessionStorage.setItem("google_state", state);
    sessionStorage.setItem("google_redirect_after_login", "/"); // go back to home

    // Build authorization URL
    const authUrl = buildAuthorizationUrl(codeChallenge, state);

    // Redirect to Google
    window.location.href = authUrl;
  } catch (error) {
    console.error("Google login error:", error);
    throw error;
  }
}

/**
 * Handle the OAuth callback - Exchange code for token and redirect.
 * Call this function on your callback page after Google redirects back.
 *
 * @param {Object} options - Configuration options
 * @param {function} options.onSuccess - Callback on successful authentication
 * @param {function} options.onError - Callback on error
 * @returns {Promise<void>}
 */
export async function handleGoogleCallback({
  onSuccess = null,
  onError = null,
  postLoginAction = null,
} = {}) {
  try {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const receivedState = urlParams.get("state");
    const error = urlParams.get("error");

    // Check for errors from Google
    if (error) {
      sessionStorage.removeItem("google_code_verifier");
      sessionStorage.removeItem("google_state");
      const errorObj = new Error(`Google OAuth error: ${error}`);
      if (onError) {
        onError(errorObj);
      } else {
        throw errorObj;
      }
      return;
    }

    // Validate required parameters
    if (!code || !receivedState) {
      throw new Error("Missing authorization code or state parameter");
    }

    // Retrieve stored PKCE parameters
    const codeVerifier = sessionStorage.getItem("google_code_verifier");
    const storedState = sessionStorage.getItem("google_state");

    if (!codeVerifier || !storedState) {
      throw new Error(
        "PKCE parameters not found. Please restart the login process."
      );
    }

    // Verify state to prevent CSRF attacks
    if (storedState !== receivedState) {
      sessionStorage.removeItem("google_code_verifier");
      sessionStorage.removeItem("google_state");
      throw new Error("Invalid state parameter - possible CSRF attack");
    }

    // Exchange authorization code for tokens via backend
    const apiResponse = await axiosInstance.post("login/google/token", {
      code,
      code_verifier: codeVerifier,
      origin: window.location.origin
    });

    // Axios automatically parses JSON and puts it in response.data
    const data = apiResponse.data;

    // Check if the API response indicates success
    if (!data || data.status === "error" || !data.data) {
      throw new Error(data?.message || "Failed to exchange authorization code");
    }

    // Clear PKCE parameters after successful exchange
    sessionStorage.removeItem("google_code_verifier");
    sessionStorage.removeItem("google_state");
    sessionStorage.removeItem("google_redirect_after_login");

    Swal.fire({
      title: "Logging in...",
      text: "Please wait...",
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading(); // Display the loading spinner
      },
    });

    const {
      user: { id, email, name },
    } = data.data;

    const {
      tokens: { id_token },
    } = data.data;

    const params = {
      google_id: id,
      email,
      name,
			id_token,
      device_type: deviceType,
    };

    // Store this in sessionStorage so that route /registerSocialMedia can read when needed.
    sessionStorage.setItem("google_user_info", JSON.stringify(params));

    // Fetch IP addresses (IPv4 and IPv6) in parallel
    const ipv4Promise = axios
      .get("https://api4.my-ip.io/v2/ip.json")
      .then((response) => {
        if (response.data) {
          params.ipv4 = response.data.ip;
        }
      })
      .catch((error) => {
        console.warn("Failed to fetch IPv4:", error);
      });

    const ipv6Promise = axios
      .get("https://api6.my-ip.io/v2/ip.json")
      .then((response) => {
        if (response.data) {
          params.ipv6 = response.data.ip;
        }
      })
      .catch((error) => {
        console.warn("Failed to fetch IPv6:", error);
      });

    // Wait for both IP fetches to complete
    await Promise.all([ipv4Promise, ipv6Promise]);

    // Encrypt the data with trxTime and trxHash
    const encryptedData = encryptionAuth(params);

    // Call the postLogin action with encrypted data
    postLoginAction(encryptedData);

    // Redirect back to original page
    // window.location.href = redirectPath;
  } catch (error) {
    console.error("Google callback error:", error);
    sessionStorage.removeItem("google_code_verifier");
    sessionStorage.removeItem("google_state");

    const currentLang = localStorage.getItem("lang") || "en";
    const translationTitle = {
      en: "Error",
      zh_cn: "错误",
      ms: "Kesilapan",
      th: "ข้อผิดพลาด",
      id: "Kesalahan",
      vi: "lỗi",
      us: "Error",
    };
    const translationButton = {
      en: "OK",
      zh_cn: "好的",
      ms: "Okey",
      th: "ตกลง",
      id: "Oke",
      vi: "VÂNG",
      us: "OK",
    };

    Swal.fire({
      iconHtml:
        '<img src="https://api.iconify.design/mdi:close.svg?color=%23ffffff">',
      title: translationTitle[currentLang],
      text: error.message || "Google login failed",
      icon: "error",
      confirmButtonText: translationButton[currentLang],
      customClass: {
        container: "swal-mobile-container failed",
        icon: "swal-failed-icon",
      },
    }).then(() => {
      window.location.href = "/login";
    });
  }
}

/**
 * Verify if user is authenticated with Google.
 * @returns {boolean}
 */
export function hasGoogleAuth() {
  const accessToken = localStorage.getItem("google_access_token");
  return !!accessToken;
}

/**
 * Get stored Google user info.
 * @returns {object|null}
 */
export function getGoogleUserInfo() {
  const userInfo = localStorage.getItem("google_user_info");
  return userInfo ? JSON.parse(userInfo) : null;
}

/**
 * Get stored Google access token.
 * @returns {string|null}
 */
export function getGoogleAccessToken() {
  return localStorage.getItem("google_access_token");
}

/**
 * Clear Google authentication.
 */
export function clearGoogleAuth() {
  localStorage.removeItem("google_access_token");
  localStorage.removeItem("google_refresh_token");
  localStorage.removeItem("google_user_info");
}
